import { BorderStyle, Footer, HeadingLevel, PageNumber, Paragraph, Tab, Table, TableCell, TableLayoutType, TableRow, TabStopType, TextRun, WidthType } from 'docx';
import { mathTextRuns } from './mathDocument';

export const PAGE_WIDTH = 11906;
export const PAGE_HEIGHT = 16838;
export const MARGIN = 1134;
export const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
export const rule = { style: BorderStyle.SINGLE, size: 4, color: '808080' };

export function paragraph(text, options = {}) {
    return new Paragraph({ ...options, children: mathTextRuns(text) });
}

export function heading(text, pageBreakBefore = false) {
    return paragraph(text, { heading: HeadingLevel.HEADING_1, pageBreakBefore });
}

export function markedParagraph(number, text, marks, options = {}) {
    return new Paragraph({
        indent: { left: 640, hanging: 640, right: 640 },
        tabStops: [{ type: TabStopType.LEFT, position: 640 }, { type: TabStopType.RIGHT, position: CONTENT_WIDTH }],
        spacing: { before: 180, after: 120 },
        ...options,
        children: [
            new TextRun({ children: [String(number), new Tab()], bold: true }),
            ...mathTextRuns(text),
            new TextRun({ children: [new Tab(), `(${marks ?? 0})`] })
        ]
    });
}

export function answerLines(value) {
    const parsed = Number(value);
    const count = Number.isFinite(parsed) && value != null ? Math.min(12, Math.max(0, Math.floor(parsed))) : 2;
    return Array.from({ length: count }, (_, index) => paragraph('', {
        indent: { left: 640, right: 640 },
        border: { bottom: rule },
        spacing: { before: 100, after: 100, line: 360 },
        keepNext: index < count - 1
    }));
}

export function grid(headers, rows, widths) {
    return new Table({
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        columnWidths: widths,
        layout: TableLayoutType.FIXED,
        indent: { size: 120, type: WidthType.DXA },
        margins: { top: 100, bottom: 100, left: 120, right: 120 },
        rows: [headers, ...rows].map((values, rowIndex) => new TableRow({
            tableHeader: rowIndex === 0,
            children: values.map((value, columnIndex) => new TableCell({
                width: { size: widths[columnIndex], type: WidthType.DXA },
                ...(rowIndex === 0 ? { shading: { fill: 'EEEEEE' } } : {}),
                children: [new Paragraph({
                    spacing: { after: 0 },
                    children: mathTextRuns(value, { bold: rowIndex === 0, size: 20 })
                })]
            }))
        }))
    });
}

export const assessmentStyles = {
            default: { document: { run: { font: 'Arial', size: 22, color: '000000' }, paragraph: { spacing: { after: 120, line: 276 }, widowControl: true } } },
            paragraphStyles: [
                { id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', quickFormat: true, run: { font: 'Arial', size: 24, bold: true, color: '000000' }, paragraph: { spacing: { before: 280, after: 200 }, keepNext: true } },
                { id: 'Heading2', name: 'Heading 2', basedOn: 'Normal', next: 'Normal', quickFormat: true, run: { font: 'Arial', size: 22, bold: true, color: '000000' }, paragraph: { spacing: { before: 200, after: 120 }, keepNext: true } }
            ]
        };

export const assessmentPage = { size: { width: PAGE_WIDTH, height: PAGE_HEIGHT }, margin: { top: MARGIN, bottom: MARGIN, left: MARGIN, right: MARGIN, footer: 567 } };

export function assessmentFooter(grade, subject, label, gradeLabel = 'Grade') {
    return new Footer({ children: [new Paragraph({
        tabStops: [{ type: TabStopType.RIGHT, position: CONTENT_WIDTH }],
        children: [new TextRun({ children: [`${gradeLabel} ${grade} ${subject} | ${label}`, new Tab()], size: 18 }), new TextRun({ children: [PageNumber.CURRENT], size: 18 })]
    })] });
}

export function curriculumSourceParagraphs(sources = [], translate = (text) => text, notice = '') {
    if (!sources.length && !notice) return [];
    return [
        heading(translate('Curriculum sources')),
        ...(notice ? [paragraph(translate(notice))] : []),
        ...sources.flatMap((source) => [
            paragraph(`${source.title} (${source.language})`),
            paragraph(source.url),
            ...(source.pages?.length ? [paragraph(`${translate('PDF pages')}: ${source.pages.join(', ')}`)] : []),
            paragraph(`${translate('Retrieved')}: ${source.retrievedAt}`)
        ]),
        paragraph(translate('Review against your current ATP and applicable CAPS amendments before use.'))
    ];
}
